import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import SavingsIcon from '@mui/icons-material/Savings';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GROUP_TYPES, RECORD_STATUSES } from '@app/shared';
import type { GroupDto } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { StatusChip } from '../../components/StatusChip';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { formatDate } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import {
  useAssignGroups,
  useCopyGroup,
  useCreateGroup,
  useDeleteGroup,
  useGroupOptions,
  useGroups,
  useReplaceGroupPermissions,
  useUpdateGroup,
} from './api';

type NameDialog = { mode: 'create' } | { mode: 'copy'; source: GroupDto } | null;
type LimitKey = 'discountCreditLimitCents' | 'walletLimitCents';
/**
 * The reference's two limit dialogs. Assumption: its rule was not seen; here a limit is the most
 * one member may put into a customer's discount credit or wallet in one transaction.
 */
const LIMIT_META: Record<LimitKey, { title: string; label: string }> = {
  discountCreditLimitCents: {
    title: 'Manage discount credit limit',
    label: 'Discount credit limit ($)',
  },
  walletLimitCents: { title: 'Manage wallet limit', label: 'Wallet limit ($)' },
};

/**
 * Manage user groups (PC12): list, add, copy, replace permissions and delete groups. Permissions
 * are edited on the group page.
 */
export function GroupsPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  const list = useListState({ q: '', status: '', type: '' }, 10, 'name');
  const groups = useGroups(list.params);
  const createGroup = useCreateGroup();
  const copyGroup = useCopyGroup();
  const deleteGroup = useDeleteGroup();

  const [nameDialog, setNameDialog] = useState<NameDialog>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [toDelete, setToDelete] = useState<GroupDto | null>(null);
  const [replacing, setReplacing] = useState<GroupDto | null>(null);
  const [sourceId, setSourceId] = useState('');
  const [sourceError, setSourceError] = useState('');
  const replace = useReplaceGroupPermissions();
  const featureOn = useFeatureReleased();
  const updateGroup = useUpdateGroup();
  // "Manage discount credit limit" and "Manage wallet limit": empty means no limit.
  const [limitOf, setLimitOf] = useState<{ group: GroupDto; key: LimitKey } | null>(null);
  const [limitText, setLimitText] = useState('');
  const [limitError, setLimitError] = useState('');
  const openLimit = (group: GroupDto, key: LimitKey) => {
    const cents = group[key];
    setLimitText(cents === null ? '' : (cents / 100).toFixed(2));
    setLimitError('');
    setLimitOf({ group, key });
  };
  const submitLimit = async (e: FormEvent) => {
    e.preventDefault();
    if (!limitOf) return;
    const text = limitText.trim();
    if (text && !/^\d{1,7}(\.\d{1,2})?$/.test(text))
      return setLimitError('Enter an amount in dollars, or leave it empty for no limit');
    const cents = text ? Math.round(Number(text) * 100) : null;
    try {
      await updateGroup.mutateAsync({ id: limitOf.group.id, input: { [limitOf.key]: cents } });
      notify.success(`${LIMIT_META[limitOf.key].title} of "${limitOf.group.name}" saved.`);
      setLimitOf(null);
    } catch (err) {
      notify.error(err);
    }
  };
  // "Assign Group": the groups the members of a group may give. `null` = every group.
  const [assigning, setAssigning] = useState<GroupDto | null>(null);
  const [ticked, setTicked] = useState<string[] | null>(null);
  const [assignError, setAssignError] = useState('');
  const assign = useAssignGroups();
  const options = useGroupOptions(Boolean(replacing) || Boolean(assigning));
  const allIds = (options.data ?? []).map((o) => o.id);
  const tickedIds = ticked ?? allIds;
  const toggle = (id: string) => {
    setAssignError('');
    setTicked(tickedIds.includes(id) ? tickedIds.filter((x) => x !== id) : [...tickedIds, id]);
  };
  const submitAssign = async (e: FormEvent) => {
    e.preventDefault();
    if (!assigning) return;
    if (tickedIds.length === 0) return setAssignError('Select at least one group');
    try {
      await assign.mutateAsync({ id: assigning.id, groupIds: tickedIds });
      notify.success(`Assign Group of "${assigning.name}" saved.`);
      setAssigning(null);
    } catch (err) {
      if (err instanceof ApiError && err.details.some((d) => d.path === 'groupIds'))
        setAssignError(err.details.find((d) => d.path === 'groupIds')!.message);
      else notify.error(err);
    }
  };

  const submitReplace = async (e: FormEvent) => {
    e.preventDefault();
    if (!replacing) return;
    if (!sourceId) return setSourceError('Select source group');
    try {
      await replace.mutateAsync({ id: replacing.id, sourceGroupId: sourceId });
      notify.success(`Permissions of "${replacing.name}" replaced.`);
      setReplacing(null);
    } catch (err) {
      if (err instanceof ApiError && err.details.some((d) => d.path === 'sourceGroupId'))
        setSourceError(err.details.find((d) => d.path === 'sourceGroupId')!.message);
      else notify.error(err);
    }
  };

  const openName = (dialog: NonNullable<NameDialog>) => {
    setName(dialog.mode === 'copy' ? `${dialog.source.name} copy` : '');
    setNameError('');
    setNameDialog(dialog);
  };

  const submitName = async (e: FormEvent) => {
    e.preventDefault();
    if (!nameDialog) return;
    try {
      const group =
        nameDialog.mode === 'create'
          ? await createGroup.mutateAsync({ name })
          : await copyGroup.mutateAsync({ id: nameDialog.source.id, name });
      setNameDialog(null);
      notify.success(`Group "${group.name}" created. Choose its permissions.`);
      navigate(`/settings/users/groups/${group.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.details.some((d) => d.path === 'name'))
        setNameError(err.details.find((d) => d.path === 'name')!.message);
      else setNameError(err instanceof Error ? err.message : 'Could not save');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteGroup.mutateAsync(toDelete.id);
      notify.success(`Group "${toDelete.name}" deleted.`);
    } catch (err) {
      notify.error(err);
    } finally {
      setToDelete(null);
    }
  };

  const columns: Column<GroupDto>[] = [
    { key: '#', header: '#', render: (_g, i) => rowNumber(i, list.page, list.pageSize), width: 50 },
    { key: 'name', header: 'Group name', sortable: true, render: (g) => g.name },
    { key: 'type', header: 'Group type', sortable: true, render: (g) => g.type },
    { key: 'employees', header: 'Employees', align: 'right', render: (g) => g.members.employees },
    { key: 'retailers', header: 'Retailers', align: 'right', render: (g) => g.members.retailers },
    {
      key: 'distributors',
      header: 'Distributors',
      align: 'right',
      render: (g) => g.members.distributors,
    },
    { key: 'masters', header: 'Masters', align: 'right', render: (g) => g.members.masters },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (g) => <StatusChip status={g.status} />,
    },
    {
      key: 'createdAt',
      header: 'Created on',
      sortable: true,
      nowrap: true,
      render: (g) => formatDate(g.createdAt),
    },
    {
      key: 'actions',
      header: 'Actions',
      nowrap: true,
      render: (g) => (
        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Manage permission">
            <IconButton
              size="small"
              component={Link}
              to={`/settings/users/groups/${g.id}`}
              aria-label={`Manage permission for ${g.name}`}
            >
              <LockPersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy group">
            <IconButton
              size="small"
              onClick={() => openName({ mode: 'copy', source: g })}
              aria-label={`Copy group ${g.name}`}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {featureOn('group-limits') ? (
            <>
              <Tooltip title="Manage discount credit limit">
                <IconButton
                  size="small"
                  onClick={() => openLimit(g, 'discountCreditLimitCents')}
                  aria-label={`Manage discount credit limit of ${g.name}`}
                >
                  <SavingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Manage wallet limit">
                <IconButton
                  size="small"
                  onClick={() => openLimit(g, 'walletLimitCents')}
                  aria-label={`Manage wallet limit of ${g.name}`}
                >
                  <AccountBalanceWalletIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
          {featureOn('group-replace-permissions') ? (
            <Tooltip title="Replace Group Permissions">
              <IconButton
                size="small"
                onClick={() => {
                  setSourceId('');
                  setSourceError('');
                  setReplacing(g);
                }}
                aria-label={`Replace permissions of ${g.name}`}
              >
                <SyncAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {featureOn('group-assign') ? (
            <Tooltip title="Assign Group">
              <IconButton
                size="small"
                onClick={() => {
                  setTicked(g.assignableGroupIds);
                  setAssignError('');
                  setAssigning(g);
                }}
                aria-label={`Assign Group of ${g.name}`}
              >
                <GroupAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {g.type === 'Custom' ? (
            <Tooltip title="Delete group">
              <IconButton
                size="small"
                onClick={() => setToDelete(g)}
                aria-label={`Delete group ${g.name}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        {featureOn('group-export') ? (
          // The groups the filters applied with Search pick, as a CSV file.
          <Button
            component="a"
            href={api.url('/user-groups/export', list.applied)}
            download
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export
          </Button>
        ) : null}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openName({ mode: 'create' })}
        >
          Add group
        </Button>
      </Box>
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Search group name"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
        />
        <TextField
          select
          label="Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">Status: All</MenuItem>
          {RECORD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Group type"
          value={list.draft.type}
          onChange={(e) => list.setField('type', e.target.value)}
        >
          <MenuItem value="">Group type: All</MenuItem>
          {GROUP_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <DataTable
        label="User groups"
        columns={columns}
        rows={groups.data?.data}
        rowKey={(g) => g.id}
        loading={groups.isFetching}
        error={groups.error}
        onRetry={() => void groups.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={groups.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />

      <FormDialog
        open={Boolean(nameDialog)}
        title={nameDialog?.mode === 'copy' ? 'Copy Group' : 'Add group'}
        onClose={() => setNameDialog(null)}
        onSubmit={(e) => void submitName(e)}
        submitting={createGroup.isPending || copyGroup.isPending}
        maxWidth="xs"
      >
        <TextField
          autoFocus
          required
          label="Enter new group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={Boolean(nameError)}
          helperText={
            nameError ||
            (nameDialog?.mode === 'copy'
              ? `Copies the permissions of "${nameDialog.source.name}".`
              : ' ')
          }
          sx={{ mt: 1 }}
        />
      </FormDialog>

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete group"
        message={`Delete the group "${toDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete group"
        danger
        busy={deleteGroup.isPending}
        onConfirm={() => void confirmDelete()}
        onClose={() => setToDelete(null)}
      />

      <FormDialog
        open={Boolean(limitOf)}
        title={limitOf ? LIMIT_META[limitOf.key].title : ''}
        onClose={() => setLimitOf(null)}
        onSubmit={(e) => void submitLimit(e)}
        submitting={updateGroup.isPending}
      >
        <TextField
          fullWidth
          label={limitOf ? LIMIT_META[limitOf.key].label : ''}
          value={limitText}
          onChange={(e) => {
            setLimitText(e.target.value);
            setLimitError('');
          }}
          error={Boolean(limitError)}
          helperText={
            limitError || 'The most a member may put in at a time. Leave empty for no limit.'
          }
          slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 10 } }}
          sx={{ mt: 1 }}
        />
      </FormDialog>

      <FormDialog
        open={Boolean(assigning)}
        title="Assign Group"
        onClose={() => setAssigning(null)}
        onSubmit={(e) => void submitAssign(e)}
        submitting={assign.isPending}
        cancelLabel="Close"
      >
        <Typography variant="subtitle2">Assign this group to users</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Members of &quot;{assigning?.name}&quot; may give only the ticked groups to the users they
          add or edit.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            label="Select All"
            control={
              <Checkbox
                checked={allIds.length > 0 && tickedIds.length === allIds.length}
                indeterminate={tickedIds.length > 0 && tickedIds.length < allIds.length}
                onChange={(e) => {
                  setAssignError('');
                  setTicked(e.target.checked ? allIds : []);
                }}
              />
            }
          />
          {(options.data ?? []).map((o) => (
            <FormControlLabel
              key={o.id}
              label={o.name}
              control={
                <Checkbox checked={tickedIds.includes(o.id)} onChange={() => toggle(o.id)} />
              }
            />
          ))}
        </Box>
        {assignError ? <FormHelperText error>{assignError}</FormHelperText> : null}
      </FormDialog>

      <FormDialog
        open={Boolean(replacing)}
        title="Replace Group Permissions"
        onClose={() => setReplacing(null)}
        onSubmit={(e) => void submitReplace(e)}
        submitting={replace.isPending}
      >
        <TextField
          select
          fullWidth
          required
          label="Select source group"
          value={sourceId}
          onChange={(e) => {
            setSourceId(e.target.value);
            setSourceError('');
          }}
          error={Boolean(sourceError)}
          helperText={
            sourceError ||
            `"${replacing?.name}" will have exactly the permissions of the group chosen here.`
          }
          sx={{ mt: 1 }}
        >
          {(options.data ?? [])
            .filter((o) => o.id !== replacing?.id)
            .map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
        </TextField>
      </FormDialog>
    </Box>
  );
}
